/** How one item of a `runPooled` queue turned out. */
export type PooledOutcome<T, R> = { item: T; ok: true; value: R } | { item: T; ok: false; error: unknown };

/**
 * Run `work` over every item with at most `concurrency` in flight, starting the next item as soon as one finishes. A
 * failing item doesn't stop the rest; each outcome is reported, in the items' order.
 */
export async function runPooled<T, R>(
	items: readonly T[],
	concurrency: number,
	work: (item: T) => Promise<R>,
): Promise<PooledOutcome<T, R>[]> {
	const outcomes: PooledOutcome<T, R>[] = new Array(items.length);
	let next = 0;
	const worker = async () => {
		while (next < items.length) {
			const index = next++;
			const item = items[index];
			try {
				outcomes[index] = { item, ok: true, value: await work(item) };
			} catch (error) {
				outcomes[index] = { item, ok: false, error };
			}
		}
	};
	const workers = Math.min(Math.max(1, concurrency), items.length);
	await Promise.all(Array.from({ length: workers }, worker));
	return outcomes;
}
